import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { inject } from 'mobx-react'
import moment from 'moment'
import { OptionsObject } from 'notistack'
import React, { useEffect, useState } from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { Priority } from 'src/models/enums/Priority.enum'
import {
  getInboxes,
  GetUserOptions,
  processBulkAction,
} from 'src/services/WorkflowService'
import { InboxItemType } from 'src/utils/InboxItemType'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { IStores } from '../../../Stores'
import { UserDropdownOption } from '../../../viewModels/UserDropdownOption'
import { BulkActionItem } from '../../../viewModels/workflow/BulkActionItem'
import { BulkActionResult } from '../../../viewModels/workflow/BulkActionResult'
import { ObjectiveBulkProcessAction } from '../../../viewModels/workflow/ObjectiveBulkProcessAction'
import { AssignToEntryUserAndSendValidationSchema } from '../../validationSchemas/AssignToEntryUserAndSendValidationSchema'
import BulkActionResultDisplay from './BulkActionResultDisplay'

const PRIORITY_OPTIONS: PriorityOption[] = Object.keys(Priority)
  .filter((key) => isNaN(Number(key)))
  .map((key) => ({
    id: Priority[key as keyof typeof Priority],
    label: key,
  }))

type UserOption = { id: string; description: string }
type PriorityOption = { id: number; label: string }

interface IBulkAssignToEntryUserAndSendModalProps {
  isOpen: boolean
  onClose: (actionExecuted: boolean) => void
  action: InboxActionSummary
  itemType?: InboxItemType
  inboxId: string
  userOptions?: UserOption[]
  getUserOptions?: (inboxId: string, actionId: string) => void
  assignDueDate?: string
  isFixed?: boolean
  inboxItems: Array<BulkActionItem>
  sendNotification?: (
    key: string,
    message: string,
    options?: OptionsObject,
    information?: string | undefined
  ) => void
}

function BulkAssignToEntryUserAndSendModal(
  this: any,
  props: IBulkAssignToEntryUserAndSendModalProps
) {
  const { isOpen, action, itemType, inboxId, isFixed = true, inboxItems } = props

  const [bulkAssignAndSendIsLoading, setBulkAssignAndSendIsLoading] = useState(false)
  const [executedBulkAssignAndSend, setBulkAssignAndSendExecuted] = useState(false)
  const [executingBulkAssignAndSendAction, setBulkAssignAndSendExecuting] =
    useState(false)
  const [resultsBulkAssignAndSend, setBulkAssignAndSendResults] = useState(
    new Array<BulkActionResult>()
  )
  const [sendToOptions, setBulkSendToOptions] = useState<UserOption[]>([])
  const [userOptions, setUserOptions] = useState(new Array<UserDropdownOption>())

  useEffect(() => {
    const initializeModal = async () => {
      setBulkAssignAndSendIsLoading(true)
      setBulkSendToOptions([])

      try {
        const inboxes = await getInboxes(itemType, isFixed)
        const filteredOptions: UserOption[] = inboxes
          .filter((inbox: any) => inbox.enabled)
          .map((inbox: any) => ({
            id: String(inbox.id),
            description: String(inbox.inboxName),
          }))
        setBulkSendToOptions(filteredOptions)
      } catch {
        notifyAndClose('Failed to fetch inbox list.')
        return
      }

      if (inboxId) {
        GetUserOptions(props.inboxId, props.action.id.toString())
          .then((response) => {
            setUserOptions(response)
          })
          .catch(() => {
            setUserOptions(new Array<UserDropdownOption>())
          })
      }

      setBulkAssignAndSendIsLoading(false)
    }

    if (isOpen) {
      initializeModal().then(() => {})
    }
  }, [isOpen, itemType, inboxId, action.id, isFixed])

  const notifyAndClose = (message: string) => {
    props.sendNotification?.('404', message, { variant: 'error' })
    handlesOnClose()
  }
  const handlesOnClose = () => {
    props.onClose(executedBulkAssignAndSend)
  }

  async function processAction(values: any) {
    setBulkAssignAndSendExecuting(true)
    const action = props.action

    const data = new ObjectiveBulkProcessAction()
    data.bulkInboxItems = inboxItems
    data.comment = values.comments
    data.assignUser = values.assignUser ? values.assignUser.id : undefined
    data.nextInboxIdOverride = values.selectedSendTo?.id
      ? Number(values.selectedSendTo.id)
      : undefined
    data.priorityOverride = values.priority?.id

    if (values.dueDate && values.assignUser) {
      const [year, month, day] = values.dueDate.split('-').map(Number)

      data.assignDueDate = new Date(year, month - 1, day, 16, 0, 0, 0)
    } else {
      data.assignDueDate = undefined
    }

    if (values.pendDate) {
      data.pendDate = new Date(moment(values.pendDate, 'YYYY-MM-DD').toISOString())
      data.pendNote = values.pendNote
    } else {
      data.pendDate = undefined
      data.pendNote = undefined
    }

    try {
      const response = await processBulkAction(data, action.id.toString())
      setBulkAssignAndSendResults(response)
      setBulkAssignAndSendExecuting(false)
      setBulkAssignAndSendExecuted(true)
    } catch (error) {
      setBulkAssignAndSendExecuting(false)
    }
  }

  return (
    <Dialog
      key={action.id}
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return this.onClose()
      }}
      scroll="body"
      PaperProps={{ style: { overflow: 'visible' } }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={action.actionName.toUpperCase()} isModal />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px', overflow: 'visible' }}>
        {(executingBulkAssignAndSendAction || bulkAssignAndSendIsLoading) && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1200,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={{
            comments: '',
            assignUser: null,
            selectedSendTo: null,
            priority: PRIORITY_OPTIONS.find((p) => p.label === 'Normal') || null,
            dueDate: '',
            requireComment: props.action.requireComment ?? false,
            pendDate: '',
            pendNote: '',
          }}
          validationSchema={AssignToEntryUserAndSendValidationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          enableReinitialize
          onSubmit={() => {}}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            isValid,
            touched,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                {!executedBulkAssignAndSend && (
                  <>
                    <Grid style={{ paddingBottom: '3px' }} item>
                      <Typography variant="h6">Assignment and Routing Details</Typography>
                    </Grid>
                    <Grid item>
                      <FormControl fullWidth variant="outlined">
                        <Autocomplete
                          size="small"
                          options={sendToOptions}
                          getOptionLabel={(option: UserOption) =>
                            option?.description || ''
                          }
                          value={values.selectedSendTo}
                          onChange={(_, value) => setFieldValue('selectedSendTo', value)}
                          onBlur={() => setFieldTouched('selectedSendTo', true)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Send To Inbox"
                              required
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              error={Boolean(
                                touched.selectedSendTo && errors.selectedSendTo
                              )}
                              helperText={touched.selectedSendTo && errors.selectedSendTo}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <FormControl fullWidth variant="outlined">
                        <Autocomplete
                          size="small"
                          options={userOptions || []}
                          getOptionLabel={(option: any) => option?.description || ''}
                          value={values.selectedSendTo ? values.assignUser : null}
                          onChange={(_, value) => setFieldValue('assignUser', value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Assigning To"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          )}
                          disabled={!values.selectedSendTo}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          inputProps={{ id: 'assignDueDate' }}
                          InputLabelProps={{ shrink: true }}
                          type="date"
                          name="dueDate"
                          variant="outlined"
                          value={values.dueDate}
                          label="Assign Due Date"
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.dueDate && touched.dueDate}
                          helperText={touched.dueDate && errors.dueDate}
                          InputProps={{ style: { height: '40px' } }}
                          disabled={!values.assignUser}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Autocomplete
                          disablePortal
                          size="small"
                          options={PRIORITY_OPTIONS}
                          getOptionLabel={(option: PriorityOption) => option.label}
                          value={values.priority}
                          onChange={(_, value) => setFieldValue('priority', value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Priority"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid style={{ paddingBottom: '3px' }} item>
                      <Typography variant="h6">Pend</Typography>
                    </Grid>
                    <Grid item container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          inputProps={{ id: 'pendDate' }}
                          InputLabelProps={{ shrink: true }}
                          type="date"
                          name="pendDate"
                          variant="outlined"
                          value={values.pendDate}
                          label="Pend Date"
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.pendDate && touched.pendDate}
                          helperText={touched.pendDate && errors.pendDate}
                          InputProps={{ style: { height: '40px' } }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        minRows={2}
                        multiline
                        label="Pend Note"
                        name="pendNote"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={values.pendNote}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.pendNote && touched.pendNote}
                        helperText={touched.pendNote && errors.pendNote}
                      />
                    </Grid>
                    {action.displayComment && (
                      <Grid item>
                        <TextField
                          label="Comments"
                          name="comments"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          required={action.requireComment}
                          multiline
                          minRows={4}
                          value={values.comments}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.comments && touched.comments}
                          helperText={touched.comments && errors.comments}
                        />
                      </Grid>
                    )}
                  </>
                )}
                {executedBulkAssignAndSend && !executingBulkAssignAndSendAction && (
                  <Grid container direction="column" spacing={1}>
                    <Grid item xs={12} style={{ marginLeft: '8px' }}>
                      <BulkActionResultDisplay results={resultsBulkAssignAndSend} />
                    </Grid>
                  </Grid>
                )}
                <Grid item>
                  <DialogActions>
                    {!executedBulkAssignAndSend && (
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: '4px' }}
                        disabled={
                          executedBulkAssignAndSend ||
                          !isValid ||
                          executingBulkAssignAndSendAction
                        }
                        onClick={() => {
                          processAction(values).then(() => {})
                        }}
                      >
                        Execute
                      </Button>
                    )}
                    <Button onClick={handlesOnClose} data-cy="close">
                      Close
                    </Button>
                  </DialogActions>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

const InjectedBulkAssignToEntryUserAndSendModal = inject<
  IStores,
  IBulkAssignToEntryUserAndSendModalProps,
  Partial<IBulkAssignToEntryUserAndSendModalProps>,
  any
>((stores: IStores) => ({
  sendNotification: stores.notifications.sendNotification,
}))(BulkAssignToEntryUserAndSendModal)

export default InjectedBulkAssignToEntryUserAndSendModal
